import { AxiosError } from "axios";
import { useCallback, type ReactNode } from "react";
import { UseFormSetError } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { ROUTES } from "@/constants/routes";
import { useCreateUserMutation } from "@/features/users/api/useCreateUserMutation";
import { UserFormContentData } from "@/features/users/components/UserForm";
import AddUserForm from "@/features/users/components/UserForm/AddUserForm";
import { handleProblemDetailsError } from "@/lib/helpers/handleProblemDetailsError";
import { useAuth } from "@/lib/hooks/useAuth";
import { useBreadcrumbs } from "@/lib/hooks/useBreadcrumbs";
import useUserRoleOptions from "@/lib/hooks/useUserRoleOptions";
import { CreateUserRequest } from "@/types/request/CreateUserRequest";
import { ValidationResponse } from "@/types/response/ValidationResponse";

type AddUserPageProps = {
  clientId?: string;
};

function AddUserPageContent({ clientId }: AddUserPageProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { tenantId, context } = useAuth();
  const roleContext = clientId ? "client" : context ?? "admin";
  const { getRoleOptions } = useUserRoleOptions(roleContext);

  const basePath = clientId
    ? `/${ROUTES.clients}/${clientId}/${ROUTES.users}`
    : `/${ROUTES.users}`;

  const handleNavigateBack = useCallback(() => {
    navigate(basePath);
  }, [navigate, basePath]);

  const resolvedTenantId = !clientId && context === "admin" ? tenantId : null;

  const addUser = useCreateUserMutation({
    tenantId: resolvedTenantId,
    clientId: clientId || null,
  });

  const handleSubmit = useCallback(
    (
      payload: CreateUserRequest,
      setError?: UseFormSetError<UserFormContentData>,
    ) => {
      addUser.mutate(payload, {
        onSuccess: () => {
          handleNavigateBack();
        },
        onError: (
          error: AxiosError<
            | ValidationResponse
            | {
                title?: string;
                detail?: string;
                status?: number;
                field?: string;
              }
          >,
        ) => {
          if (!error.response?.data) {
            return;
          }

          const responseData = error.response.data;

          if (Array.isArray(responseData)) {
            responseData.forEach(({ propertyName, errorMessage }) => {
              if (setError) {
                setError(propertyName as keyof UserFormContentData, {
                  type: "manual",
                  message: t(`errorCodes.${errorMessage}`, {
                    fieldName: t(`common.${propertyName}`),
                  }),
                });
              }
            });
            return;
          }

          handleProblemDetailsError({
            error: error as AxiosError<{
              title: string;
              detail: string;
              status?: number;
              field?: string;
            }>,
            t,
            setError,
          });
        },
      });
    },
    [addUser, handleNavigateBack, t],
  );

  const titleKey =
    context === "admin"
      ? "admin.userManagement.addUser.title"
      : "client.userManagement.addUser.title";
  const subtitleKey =
    context === "admin"
      ? "admin.userManagement.addUser.subtitle"
      : "client.userManagement.addUser.subtitle";

  const breadcrumbs = useBreadcrumbs({
    items: [
      { label: t("common.users"), href: basePath },
      { label: t("common.add") },
    ],
  });

  const content: ReactNode = (
    <AddUserForm
      options={getRoleOptions()}
      isSubmitting={addUser.isPending}
      onSubmitForm={handleSubmit}
      onSuccess={handleNavigateBack}
    />
  );

  return (
    <ModernPageLayout
      breadcrumbs={breadcrumbs}
      backbutton={{
        href: "../",
        label: t("common.back"),
      }}
      title={t(titleKey)}
      subtitle={t(subtitleKey)}
    >
      {content}
    </ModernPageLayout>
  );
}

function AddUserPage() {
  const { clientId } = useParams();
  return <AddUserPageContent clientId={clientId} />;
}

export default AddUserPage;
